import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import layoutStyles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Col, Form, Input, Row, Select, Space, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import styles from './FirmwaresPage.module.css';
import { useDispatch } from 'react-redux';
import { ProjectTwoTone } from '@ant-design/icons';
import { useFirmwares } from 'src/features/firmwares/firmwares.hooks';
import { DraftFirmware } from 'src/features/firmwares/firmwares.types';
import { useDevicesTypes } from 'src/features/devicesTypes/devicesTypes.hooks';
import { fetchFirmwareGroups } from 'src/features/firmwareGroups/firmwareGroups.state';
import { fetchDeviceTypes } from 'src/features/devicesTypes/devicesTypes.state';
import { useFirmwareGroupsValues } from 'src/features/firmwareGroups/firmwareGroups.hooks';
import { AppDispatch } from 'src/index';
import tileStyles from 'src/styles/tile.module.css';
import md5 from 'js-md5';

const FirmwaresPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const [form] = Form.useForm();
  const [{ firmwares, isPending: isFirmwaresPending }, { createNewFirmware, progress, fetchFirmwares }] =
    useFirmwares();
  const deviceGroups = useDevicesTypes();
  const firmwareGroups = useFirmwareGroupsValues();
  const [creationMode, setCreationMode] = useState<boolean>(false);
  const [firmwareFile, setFirmwareFile] = useState<File | null>(null);
  const [firmwareChecksum, setFirmwareChecksum] = useState<string>('');
  const [_, setChecksumCalculating] = useState(false);
  const [newFirmware, setNewFirmware] = useState<DraftFirmware>({
    filename: undefined,
    groupId: undefined,
    deviceTypeId: undefined,
    version: undefined,
    ng: true,
  });

  const handleFormSubmit = useCallback(() => {
    createNewFirmware({ file: firmwareFile, firmware: newFirmware, md5: firmwareChecksum });
  }, [createNewFirmware, firmwareFile, newFirmware, firmwareChecksum]);

  const handleCreateButtonClick = useCallback(() => {
    setCreationMode(true);
  }, []);

  useEffect(() => {
    dispatch(fetchFirmwareGroups({}));
    dispatch(fetchDeviceTypes({}));
  }, [dispatch]);

  useEffect(() => {
    if (creationMode && progress === 100) {
      setCreationMode(false);
      fetchFirmwares();
    }
  }, [creationMode, fetchFirmwares, progress]);

  const firmwaresOld = useMemo(() => firmwares.filter(f => !f.ng), [firmwares]);
  const firmwaresNg = useMemo(() => firmwares.filter(f => f.ng), [firmwares]);

  return (
    <MainLayout>
      <div className={layoutStyles.mainGrid}>
        <div className={layoutStyles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/settings/firmwares'>
                <Space>
                  <ProjectTwoTone />
                  Прошивки
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={layoutStyles.contentArea}>
          <Typography.Title level={2} className={layoutStyles.title}>
            Прошивки
          </Typography.Title>
          {creationMode && (
            <Spin spinning={progress > 0 && progress < 100}>
              <div>
                <Form
                  form={form}
                  validateTrigger='onBlur'
                  layout='vertical'
                  onFinish={handleFormSubmit}
                  initialValues={{ ng: newFirmware.ng ? 'ng' : 'old' }}
                >
                  <Space direction='vertical'>
                    <ProjectTwoTone className={styles.nameInputIcon} />
                    <Form.Item name='ng' noStyle>
                      <Select<string>
                        placeholder='Выбирете версию прошивки'
                        onChange={ev =>
                          setNewFirmware(e => ({
                            ...e,
                            ng: ev === 'ng',
                          }))
                        }
                      >
                        <Select.Option key={'ng'} value={'ng'}>
                          New generation
                        </Select.Option>
                        <Select.Option key={'old'} value={'old'}>
                          Old generation
                        </Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name='version'
                      valuePropName={'version'}
                      style={{ width: '100%' }}
                      rules={[
                        {
                          required: true,
                          validator: (_, value) => {
                            const re = /^(\d+)\.(\d+)\.(\d+)$/;
                            if (re.test(value.target.value)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Не верный формат версии, должен быть x.x.x'));
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder='Введите версию в формате x.x.x'
                        onChange={ev =>
                          setNewFirmware(e => ({
                            ...e,
                            version: ev.target.value,
                          }))
                        }
                      />
                    </Form.Item>
                    <Row style={{ width: '100%' }}>
                      <Space>
                        <Col xs={24} md={8}>
                          <Form.Item name='deviceTypeId' noStyle>
                            <Select<string>
                              placeholder='Выберите группу для прошивки'
                              onSelect={ev =>
                                setNewFirmware(e => ({
                                  ...e,
                                  deviceTypeId: ev,
                                }))
                              }
                            >
                              {deviceGroups.map(l => (
                                <Select.Option key={l.name} value={l.name}>
                                  {l.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name='groupId' noStyle>
                            <Select
                              placeholder='Выберите группу для прошивки'
                              onSelect={(ev: string) => setNewFirmware(e => ({ ...e, groupId: ev }))}
                            >
                              {firmwareGroups.map(l => (
                                <Select.Option key={l.id} value={l.id}>
                                  {l.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name='filename' noStyle>
                            <input
                              type='file'
                              placeholder='Выберите файл прошивки'
                              onChange={e => {
                                const file = (e.target.files as unknown as File[])[0];
                                setFirmwareFile(file);
                                if (file) {
                                  const reader = new FileReader();
                                  setChecksumCalculating(true);
                                  reader.onload = () => {
                                    setChecksumCalculating(false);
                                    setFirmwareChecksum(md5(reader.result as string));
                                  };
                                  reader.readAsArrayBuffer(file);
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Space>
                    </Row>
                    <div>MD5: {firmwareChecksum}</div>
                    <Row justify='end' style={{ width: '100%' }}>
                      <Space>
                        <Button htmlType='button' type='default' onClick={() => setCreationMode(false)}>
                          Отменить
                        </Button>
                        <Button htmlType='submit' type='primary'>
                          Создать
                        </Button>
                      </Space>
                    </Row>
                  </Space>
                </Form>
              </div>
            </Spin>
          )}
          <div>
            <div style={{ width: '100%', marginBottom: 30 }}>
              {!creationMode && !isFirmwaresPending && (
                <Typography.Title level={5}>Прошивки для версии 2.0</Typography.Title>
              )}
              {!creationMode && !isFirmwaresPending && firmwaresNg.length === 0 && (
                <div>
                  <Row justify='center' style={{ width: '100%', marginTop: 24 }}>
                    <Typography.Text>Прошивок для версии 2.0 не найдено. Попробуйте добавить одну.</Typography.Text>
                  </Row>
                </div>
              )}
              <div>
                {!creationMode &&
                  !isFirmwaresPending &&
                  firmwaresNg.map(f => (
                    <div key={f.id} className={tileStyles.listTile}>
                      <Link to={`/settings/firmwares/${f.id}`} className={tileStyles.tileName}>
                        <Space>
                          <ProjectTwoTone />
                          {f.name}
                        </Space>
                      </Link>
                      <Space align='center'>
                        {f.ng && <Tag color='blue'>New generation</Tag>}
                        {f.isActive && <Tag color='green'>Активирована</Tag>}
                        {f.isTest && <Tag color='orange'>Тестовая</Tag>}
                        {f.isPrivate && <Tag color='orange'>Приватная</Tag>}
                      </Space>
                    </div>
                  ))}
              </div>
            </div>
            <div style={{ width: '100%' }}>
              {!creationMode && !isFirmwaresPending && (
                <Typography.Title level={5}>Прошивки для версии 1.0</Typography.Title>
              )}
              {!creationMode && !isFirmwaresPending && firmwaresOld.length === 0 && (
                <div>
                  <Row justify='center' style={{ width: '100%', marginTop: 24 }}>
                    <Typography.Text>Прошивок для версии 1.0 не найдено. Попробуйте добавить одну.</Typography.Text>
                  </Row>
                </div>
              )}
              <div>
                {!creationMode &&
                  !isFirmwaresPending &&
                  firmwaresOld.map(f => (
                    <div key={f.id} className={tileStyles.listTile}>
                      <Link to={`/settings/firmwares/${f.id}`} className={tileStyles.tileName}>
                        <Space>
                          <ProjectTwoTone />
                          {f.name}
                        </Space>
                      </Link>
                      <Space align='center'>
                        {f.ng && <Tag color='blue'>New generation</Tag>}
                        {f.isActive && <Tag color='green'>Активирована</Tag>}
                        {f.isTest && <Tag color='orange'>Тестовая</Tag>}
                        {f.isPrivate && <Tag color='orange'>Приватная</Tag>}
                      </Space>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className={layoutStyles.sidebar}>
          <Space>
            <Button type='primary' onClick={handleCreateButtonClick}>
              Добавить новую прошивку
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const FirmwaresPageBlock: React.FC = () => {
  const [firmwares, { fetchFirmwares }] = useFirmwares();

  useEffect(() => {
    fetchFirmwares();
  }, [fetchFirmwares]);

  if (firmwares.isPending) {
    return (
      <MainLayout>
        <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
          <Spin size={'large'} />
        </Row>
      </MainLayout>
    );
  }

  return <FirmwaresPage />;
};

export default FirmwaresPageBlock;
